import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Truck, Plus, Edit2, Trash2 } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import {
  fetchStaffCouriers,
  fetchTemplates,
  deleteCourierThunk
} from '../../../../features/couriers/couriersThunks';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';
import {
  setPage,
  setRowsPerPage,
  setSearchFilter
} from '../../../../features/couriers/couriersSlice';
import { CreateCourierModal } from './CreateCourierModal';
import { EditCourierModal } from './EditCourierModal';

export const Couriers = () => {
  const dispatch = useDispatch();
  const {
    couriers,
    loading,
    error,
    pagination,
    filters,
    totalCount
  } = useSelector(state => state.couriers);

  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSearch !== filters.search) {
        dispatch(setSearchFilter(localSearch));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, filters.search, dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchStaffCouriers());
    dispatch(fetchTemplates());
    // The operators, for the owner of each row and for the create form.
    dispatch(fetchAdminOptions());
  }, [
    dispatch,
    filters.search,
    filters.page,
    filters.rowsPerPage
  ]);

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro que quieres eliminar este proveedor?")) {
      dispatch(deleteCourierThunk({ courierId: id, isStaff: true }));
    }
  };

  const columns = [
    {
      accessor: 'name',
      header: 'Proveedor',
      cell: (item) => {
        const keysCount = Object.keys(item.configuration || {}).length;
        return (
          <div className="flex flex-col">
            <span className="font-bold text-ninja-dark dark:text-white">
              {item.name}
            </span>
            <span className="text-xs text-text-muted mt-0.5">
              {keysCount} {keysCount === 1 ? 'key activa' : 'keys activas'}
            </span>
          </div>
        );
      }
    },
    {
      accessor: 'admin',
      header: 'Operador',
      cell: (item) => (
        <div className="flex flex-col">
          <span className="text-ninja-dark dark:text-white">{item.admin?.name || '—'}</span>
          <span className="text-xs text-text-muted mt-0.5">{item.admin?.email || ''}</span>
        </div>
      )
    },
    {
      accessor: 'updated',
      header: 'Última Actualización',
      cell: (item) => {
        const dateStr = item.updated_at || item.created_at;
        if (!dateStr) return '-';
        return <span className="text-text-secondary">{new Date(dateStr).toLocaleDateString()}</span>;
      }
    },
    {
      accessor: 'actions',
      header: 'Acciones',
      cell: (item) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Edit2}
            title="Editar Proveedor"
            aria-label="Editar Proveedor"
            onClick={() => {
              setSelectedCourier(item);
              setIsEditModalOpen(true);
            }}
          />
          <PendingButton
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            title="Eliminar Proveedor"
            aria-label="Eliminar Proveedor"
            className="hover:text-danger"
            pendingOn={deleteCourierThunk}
            pendingKey={item.id}
            onClick={() => handleDelete(item.id)}
          />
        </div>
      )
    }
  ];

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-300">
        <PageHeader
          icon={Truck}
          iconClassName="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
          title="Proveedores Externos"
          description="Las credenciales de courier configuradas por todos los operadores."
          actions={
            <Button variant="primary" icon={Plus} onClick={() => setIsModalOpen(true)}>
              Crear Courier
            </Button>
          }
        />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 flex items-center gap-3">
            <span className="font-semibold">Error:</span> {error}
          </div>
        )}

        <FilterBar variant="bare">
          <SearchInput
            variant="bare"
            value={localSearch}
            onChange={setLocalSearch}
            placeholder="Buscar proveedores..."
          />
        </FilterBar>

        <DataTable
          columns={columns}
          data={couriers}
          isLoading={loading}
          totalItems={totalCount}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          itemLabel="proveedores"
          onPageChange={(page) => dispatch(setPage(page))}
          onRowsPerPageChange={(rows) => dispatch(setRowsPerPage(rows))}
          emptyMessage="No se encontraron proveedores configurados."
        />
      </div>

      <CreateCourierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <EditCourierModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCourier(null);
        }}
        initialData={selectedCourier}
      />
    </>
  );
};
